import {Maximize2, Pencil, RefreshCw, Upload} from "lucide-react";
import {forwardRef, useImperativeHandle, useState} from "react";
import {Checkbox} from "@/components/ui/checkbox";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {useTitlePage} from "@/hooks/useTitlePage";
import {useBundleTreeActions} from "./bundleTreeStore";
import type {BundleMetadata, FontOption} from "./bundleTypes";
import {INLINE_PREVIEW_HEIGHT, INLINE_PREVIEW_WIDTH, InlineTitlePage, PopupTitlePage} from "./TitlePageView";

export type TitlePageSettingsHandle = {
  flushTitlePage: () => Promise<void>;
};

type TitlePageSettingsProps = {
  settings: BundleMetadata;
  onChange: (metadata: BundleMetadata) => void;
  matterSlug: string;
  bundleId: string;
  serverUpdatedAt?: string | null;
  fontOptions: FontOption[];
};

export const TitlePageSettings = forwardRef<TitlePageSettingsHandle, TitlePageSettingsProps>(
  ({settings, onChange, matterSlug, bundleId, serverUpdatedAt, fontOptions}, ref) => {
    const [popupOpen, setPopupOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const { setTitlePagePending } = useBundleTreeActions();

    const {
      format, previewBuffer, previewLoading, resetting,
      handleUpload, handleReset, handleEditorSaved, flushTitlePage,
    } = useTitlePage({
      settings, onChange, onPendingChange: setTitlePagePending, matterSlug, bundleId, serverUpdatedAt,
    });

    useImperativeHandle(ref, () => ({flushTitlePage}), [flushTitlePage]);

    return (
      <>
        <div className="space-y-3">
          <div className="flex items-start gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <Checkbox
              id="title-page-enabled"
              checked={settings.title_page.enabled}
              onCheckedChange={(checked) => onChange({
                ...settings,
                title_page: {...settings.title_page, enabled: checked === true}
              })}
              className="mt-0.5"
            />
            <label htmlFor="title-page-enabled" className="text-sm font-medium text-gray-700 cursor-pointer">
              Enabled
            </label>
          </div>

          {settings.title_page.enabled && (
            <div className="flex flex-col items-start space-y-3">
              {/* Actions */}
              <div className="flex items-center gap-3">
                {format === 'docx' && (
                  <button type="button"
                          className="flex items-center gap-1.5 text-sm text-crimson-600 hover:text-crimson-700 font-medium"
                          onClick={() => setPopupOpen(true)}>
                    <Pencil className="h-3.5 w-3.5"/> Edit
                  </button>
                )}
                <button type="button"
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium"
                        onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-3.5 w-3.5"/> Upload
                </button>
                {settings.title_page.edited && (
                  <button type="button" disabled={resetting}
                          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 font-medium"
                          onClick={handleReset}>
                    <RefreshCw className={`h-3.5 w-3.5 ${resetting ? 'animate-spin' : ''}`}/> Reset to default
                  </button>
                )}
              </div>

              {/* Inline preview */}
              <div
                role="button"
                tabIndex={0}
                className="rounded-lg overflow-hidden bg-white title-page-preview border border-gray-200 relative group cursor-pointer"
                style={{width: INLINE_PREVIEW_WIDTH, height: INLINE_PREVIEW_HEIGHT}}
                onClick={() => {
                  if (!previewLoading && previewBuffer) setPopupOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !previewLoading && previewBuffer) setPopupOpen(true);
                }}
              >
                {/* Expand button */}
                {previewBuffer && !previewLoading && (
                  <span
                    className="absolute top-2 right-3 z-10000 p-1.5 rounded-md bg-white/80 hover:bg-white shadow-xs border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Maximize2 className="h-3.5 w-3.5 text-gray-500"/>
                  </span>
                )}
                <InlineTitlePage buffer={previewBuffer} format={format} loading={previewLoading} fontOptions={fontOptions}/>
              </div>
            </div>
          )}
        </div>

        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Title Page</DialogTitle>
            </DialogHeader>
            <div
              className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors border-gray-300 cursor-pointer hover:border-gray-400"
              onClick={(e) => {
                e.stopPropagation();
                (e.currentTarget.querySelector('input[type="file"]') as HTMLInputElement)?.click();
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const f = e.dataTransfer.files[0];
                if (f) handleUpload(f);
              }}
            >
              <Upload className="h-8 w-8 text-gray-400 mb-2"/>
              <span className="text-sm text-gray-600 font-medium">Drop PDF or DOCX here</span>
              <span className="text-xs text-gray-400 mt-1">or click to browse</span>
              <input type="file" accept=".pdf,.docx" className="hidden" onClick={(e) => e.stopPropagation()}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (f) { handleUpload(f); setUploadDialogOpen(false); }
                       e.target.value = '';
                     }}/>
            </div>
          </DialogContent>
        </Dialog>
        <PopupTitlePage
          open={popupOpen}
          onOpenChange={setPopupOpen}
          onSaved={handleEditorSaved}
          buffer={previewBuffer}
          format={format}
          fontOptions={fontOptions}
        />
      </>
    );
  },
);

TitlePageSettings.displayName = 'TitlePageSettings';
